import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ModelInfo, CreativeMode, ReferenceType, FeedImagePayload } from '@/services/types';
import assetService from '@/services/amai/asset.service';
import { hasRefTypeOptions } from '@/features/generation/model-core';
import { reconstructPromptFromMetaList } from '@/utils/genImmediate';

interface GenerationSettingsState {
  prompt: string;
  // 每个模型独立的 prompt（用于 @mention 功能：切换模型时保存/恢复带 @图片 的 prompt）
  modelPrompts: Record<string, string>;
  selectedModels: string[];
  aspectRatio: string;
  creativeMode: CreativeMode;
  generationCount: number;
  refAsset: { id: string; url: string } | null;
  // 新增：临时保存的参考图数据，用于模型切换时的状态恢复
  savedRefAsset: { id: string; url: string } | null;
  refAssetArr: { id: string; url: string }[] | null;
  // 新增：临时保存的参考图数据，用于模型切换时的状态恢复
  refType: ReferenceType;
  faceFidelity: number;
  subjectFidelity: number;
  referenceStrength: number;
  // 即梦4.5等模型支持的清晰度和宽高设置
  resolution?: string;  // 清晰度，例如 "2K", "4K"
  width?: number;       // 图片宽度
  height?: number;      // 图片高度
  quality?: string;     // 图像质量，如 "low" | "medium" | "high"（GPT Image 2 等模型支持）
}

const initialState: GenerationSettingsState = {
  prompt: '',
  modelPrompts: {},
  selectedModels: [],
  refAssetArr: [],
  aspectRatio: '16:9',
  creativeMode: CreativeMode.Normal,
  generationCount: 4,
  refAsset: null,
  savedRefAsset: null,
  refType: ReferenceType.Variation,
  faceFidelity: 50,
  subjectFidelity: 50,
  referenceStrength: 50,
  resolution: undefined,
  width: undefined,
  height: undefined,
  quality: undefined,
};

export const applyImagePayloadWithAsset = createAsyncThunk(
  'imageGeneration/applyImagePayloadWithAsset',
  async (payload: FeedImagePayload, { rejectWithValue }) => {
    try {
      let refAsset: { id: string; url: string } | null = null;
      let refAssetArr: { id: string; url: string }[] = [];

      // 先用 meta_list 重建 prompt，从中提取所有 mention 的 asset ID
      // 这样可以捕获到 ref_images 之外新增的图片 ID
      const MENTION_ID_RE = /\[@(?:图片|视频|音频)\d+:([^\]]+)\]/g;
      const allMentionIds = new Set<string>();

      // 1. 收集 ref_images 中的 ID
      for (const id of (payload.ref_images || [])) {
        allMentionIds.add(id);
      }

      // 2. 用 meta_list 重建 prompt，提取其中所有 mention 的 ID（可能包含 ref_images 之外的）
      if (payload.meta_list && payload.meta_list.length > 0) {
        const reconstructedPrompt = reconstructPromptFromMetaList(
          payload.meta_list,
          payload.ref_images || [],
          [],
          [],
        );
        for (const match of reconstructedPrompt.matchAll(MENTION_ID_RE)) {
          if (match[1]) allMentionIds.add(match[1]);
        }
      }

      // 用于存储最终重建的 prompt（可能包含从 API 补全的额外 ID）
      let finalReconstructedPrompt: string | undefined;

      // 调用接口获取所有资产的详细信息（特别是 URL）
      if (allMentionIds.size > 0) {
        const assetIdList = Array.from(allMentionIds);
        const res = await assetService.getAssetList({ asset_id_list: assetIdList });
        if (res.data?.list?.length > 0) {
          const list = res.data.list;
          const urlMap = new Map<string, string>();
          list.forEach((item: any) => {
            if (item.asset_id && item.url) {
              urlMap.set(item.asset_id, item.url);
            }
          });
          // 按 payload.ref_images 的顺序构建 refAssetArr，确保索引与 @图片N 编号一致
          for (const id of (payload.ref_images || [])) {
            const url = urlMap.get(id);
            if (url) {
              refAssetArr.push({ id, url });
            }
          }
          // 将不在 ref_images 中的其他资产追加到末尾（用户后来新增的图片）
          const extraImageIds: string[] = [];
          for (const id of assetIdList) {
            if (!refAssetArr.some(a => a.id === id)) {
              const url = urlMap.get(id);
              if (url) {
                refAssetArr.push({ id, url });
                extraImageIds.push(id);
              }
            }
          }

          // 如果有 extraImageIds（说明 ref_images 不完整），用完整 ID 列表重新构建 prompt
          // 确保 meta_list 中所有 image 类型的 item 都能匹配到 ID
          if (extraImageIds.length > 0 && payload.meta_list && payload.meta_list.length > 0) {
            const completeRefImages = [...(payload.ref_images || []), ...extraImageIds];
            finalReconstructedPrompt = reconstructPromptFromMetaList(
              payload.meta_list,
              completeRefImages,
              [],
              [],
            );
          }

          if (refAssetArr.length > 0) {
            refAsset = { id: refAssetArr[0].id, url: refAssetArr[0].url };
          }
        } else {
          console.error(`重新编辑：无法获取资产ID ${assetIdList} 的详情`);
        }
      }
      // 将原始的payload和获取到的资产信息一起返回
      return { payload, refAsset, refAssetArr, finalReconstructedPrompt };
    } catch (error: any) {
      console.error('处理图片回显时出错:', error);
      return rejectWithValue(error.message);
    }
  }
);

const imageGenerationSlice = createSlice({
  name: 'imageGeneration',
  initialState,
  reducers: {
    setPrompt: (state, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setModelPrompt: (state, action: PayloadAction<{ modelId: string; prompt: string }>) => {
      if (!state.modelPrompts) state.modelPrompts = {};
      state.modelPrompts[action.payload.modelId] = action.payload.prompt;
    },
    /**
     * 单选模型：整体替换当前选中项（数组长度恒为 1，保持与后端 model_id_list 结构一致）
     */
    setSelectedModel: (state, action: PayloadAction<string>) => {
      const modelId = action.payload;
      if (state.selectedModels[0] === modelId) return;

      // 参考图状态恢复逻辑：切到不支持单图参考（无 ref_type）的模型时暂存并清空，切回时恢复
      if (!hasRefTypeOptions(modelId)) {
        if (state.refAsset && !state.savedRefAsset) {
          state.savedRefAsset = state.refAsset;
        }
        state.refAsset = null;
      } else if (!state.refAsset && state.savedRefAsset) {
        state.refAsset = state.savedRefAsset;
        state.savedRefAsset = null;
      }

      state.selectedModels = [modelId];
    },
    setAspectRatio: (state, action: PayloadAction<string>) => {
      state.aspectRatio = action.payload;
    },
    setCreativeMode: (state, action: PayloadAction<CreativeMode>) => {
      state.creativeMode = action.payload;
    },
    setGenerationCount: (state, action: PayloadAction<number>) => {
      state.generationCount = action.payload;
    },
    setRefAsset: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      state.refAsset = action.payload;
      console.log(state, action.payload, state.refAsset, 'state.refAssetArr')
      // state.refAssetArr = action.payload;
      // 当用户主动设置新的参考图时，清空保存的参考图
      if (action.payload) {
        state.savedRefAsset = null;
      }
    },
    // setRevolation: (state, action: PayloadAction<{ value: string } | null>) => {
    //   state.resolution = action.payload;
    //   console.log(state, action.payload, state.refAsset, 'state.refAssetArr')
    //   // state.refAssetArr = action.payload;
    //   // 当用户主动设置新的参考图时，清空保存的参考图
    //   if (action.payload) {
    //     state.resolution = null;
    //   }
    // },
    setRefAssetArr: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      const payload = action.payload;
      if (payload && Array.isArray(state.refAssetArr)) {
        state.refAssetArr = [...state.refAssetArr, payload];
      }
      if(!state.refAssetArr && payload){
        state.refAssetArr = [payload];
      }
      console.log(state.refAssetArr, payload);
      console.log(state.refAssetArr, action.payload, state.refAsset, 'state.refAssetArr')
    },
   
    setRefAssetArrList: (state, action: PayloadAction<{ id: string; url: string }[] | null>) => {
      state.refAssetArr = action.payload ? [...action.payload] : [];
    },
    deleteAssetArr: (state, action: PayloadAction<{ id: string; url: string } | null>) => {
      const payload = action.payload;

      if (payload && Array.isArray(state.refAssetArr)) {
        let deletedArr = state.refAssetArr.filter((item)=>{
          return item.url !== payload.url;
        });
        console.log(deletedArr, 'deletedArr')
        state.refAssetArr = deletedArr
      }
      console.log(state.refAssetArr, payload);
      console.log(state.refAssetArr, action.payload, state.refAsset, 'state.refAssetArr')
    },
    // 新增：清空保存的参考图数据
    clearSavedRefAsset: state => {
      state.savedRefAsset = null;
    },
    setRefType: (state, action: PayloadAction<ReferenceType>) => {
      state.refType = action.payload;
    },
    setFaceFidelity: (state, action: PayloadAction<number>) => {
      state.faceFidelity = action.payload;
    },
    setSubjectFidelity: (state, action: PayloadAction<number>) => {
      state.subjectFidelity = action.payload;
    },
    setReferenceStrength: (state, action: PayloadAction<number>) => {
      state.referenceStrength = action.payload;
    },
    // 新增：设置清晰度
    setResolution: (state, action: PayloadAction<string>) => {
      state.resolution = action.payload;
    },
    // 新增：设置图片宽度
    setWidth: (state, action: PayloadAction<number>) => {
      state.width = action.payload;
    },
    // 新增：设置图片高度
    setHeight: (state, action: PayloadAction<number>) => {
      state.height = action.payload;
    },
    // 新增：设置图像质量（如 GPT Image 2 的低/中/高画质）
    setQuality: (state, action: PayloadAction<string | undefined>) => {
      state.quality = action.payload;
    },
    /**
     * 单选模式下恢复默认模型：优先 Banana Nano，找不到则回退到第一个可用模型
     */
    restoreDefaultModel: (state, action: PayloadAction<ModelInfo[]>) => {
      if (state.selectedModels.length === 0 && action.payload.length > 0) {
        const defaultModelId = '01K5THTW746GEAV0DMAN8GR9S8'; // Banana_Nano
        const defaultModel = action.payload.find(m => m.model_id === defaultModelId);
        state.selectedModels = [defaultModel ? defaultModel.model_id : action.payload[0].model_id];
      }
    },
    resetToInitialState: () => ({
      ...initialState,
      savedRefAsset: null, // 确保重置时清空保存的参考图
    }),
  },
  extraReducers: builder => {
    builder.addCase(applyImagePayloadWithAsset.fulfilled, (state, action) => {
      const { payload, refAsset, refAssetArr, finalReconstructedPrompt } = action.payload;

      // 防御性检查：确保payload存在
      if (!payload) {
        console.error('applyImagePayloadWithAsset: payload为空');
        return;
      }

      try {
        // 更新基础信息，使用安全的默认值
        // 优先使用 thunk 中用完整 ID 列表重建的 prompt（解决 ref_images 不完整导致的空 ID tag 问题）
        // 其次用 meta_list + 原始 ref_images 反向重建；否则直接取 prompt 字段
        let reconstructedPrompt: string;
        if (finalReconstructedPrompt) {
          reconstructedPrompt = finalReconstructedPrompt;
        } else if (payload.meta_list && payload.meta_list.length > 0) {
          reconstructedPrompt = reconstructPromptFromMetaList(
            payload.meta_list,
            payload.ref_images || [],
            [],
            [],
          );
        } else {
          reconstructedPrompt = payload.prompt || '';
        }
        state.prompt = reconstructedPrompt || state.prompt;

        // 同步写入 modelPrompts，防止 selectedModels 变化时的 @mention 保存/恢复逻辑覆盖编辑回显的 prompt
        if (payload.model_id_list?.length === 1) {
          if (!state.modelPrompts) state.modelPrompts = {};
          state.modelPrompts[payload.model_id_list[0]] = state.prompt;
        }

        // 只有当model_id_list不为空时才更新选中模型，避免覆盖已设置的模型选择
        // 单选模式：历史任务可能包含多个模型，取第一个
        if (payload.model_id_list && payload.model_id_list.length > 0) {
          state.selectedModels = [payload.model_id_list[0]];
        }

        // 验证并设置图片比例
        if (payload.image_ratio && typeof payload.image_ratio === 'string') {
          state.aspectRatio = payload.image_ratio;
        }

        // 验证并设置创意模式
        if (payload.creative_mode !== undefined && payload.creative_mode !== null) {
          state.creativeMode = payload.creative_mode;
        }

        // 验证并设置生成数量
        if (payload.number !== undefined && payload.number !== null && payload.number > 0) {
          state.generationCount = payload.number;
        }

        // 非可灵1.5模型，需要设置ref_type为0
        const modelIdList = payload?.model_id_list || [];
        let isKeMuModel = false;
        if(modelIdList.length === 1){
          isKeMuModel = modelIdList.includes('01JVKEM9JE2057KPET1K7AVQFY');
        }
        // 不是可图1.5模型，且参考图为1张，编辑时都为所带的图为参考图 refType = 0
        // 否则使用payload中的ref_type
        let refType: number;
        if (!isKeMuModel && refAssetArr.length === 1) {
          // 不是可图1.5模型，且参考图为1张，编辑时都为所带的图为参考图
          refType = 0;
        } else if (refAssetArr.length > 1) {
          // 参考图为2张及以上，编辑时都为所带的图为参考图
          refType = 0;
        } else {
          // 否则使用payload中的ref_type，如果不存在则默认为0
          refType = payload?.ref_type ?? 0;
        }

        payload.ref_type = refType;

        // 参考图需要根据ref_type做处理，当人物长相和角色特征时，给refAsset赋值，否则给refAssetArr赋值
        if (payload.ref_type === ReferenceType.Variation || payload.ref_type === ReferenceType.Reference) {
          state.refAsset = refAsset;
          state.refAssetArr = [];
        }else {
          if (refAssetArr && Array.isArray(refAssetArr) && refAssetArr.length > 0) {
            state.refAssetArr = [...refAssetArr];
          } else {
            state.refAssetArr = [];
          }
          state.refAsset = null;
        }

        // // 更新参考图及相关保真度设置
        // state.refAsset = refAsset;
        // // 更新参考图数组 refAssetArr 为空时也需要同步清空，防止沿用旧数据
        // if (refAssetArr && Array.isArray(refAssetArr) && refAssetArr.length > 0) {
        //   state.refAssetArr = [...refAssetArr];
        // } else {
        //   state.refAssetArr = [];
        // }

        // 验证并设置参考类型
        if (payload.ref_type !== undefined && payload.ref_type !== null) {
          state.refType = payload.ref_type;
        }

        // API返回的保真度值为[0,1]，UI使用的为[0,100]，需要转换
        // 使用安全的数值转换，确保值在有效范围内
        const imageFidelity = Math.max(0, Math.min(1, payload.image_fidelity ?? 0.5));
        const humanFidelity = Math.max(0, Math.min(1, payload.human_fidelity ?? 0.5));

        // 根据参考类型，更新对应的保真度滑块值
        if (state.refType === ReferenceType.Variation) {
          // 角色特征
          state.referenceStrength = Math.round(imageFidelity * 100);
          state.subjectFidelity = Math.round(humanFidelity * 100);
        } else if (state.refType === ReferenceType.Reference) {
          // 人物长相：使用image_fidelity作为faceFidelity
          state.faceFidelity = Math.round(imageFidelity * 100);
        } else {
          // 默认或其他情况
          state.referenceStrength = Math.round(imageFidelity * 100);
          state.faceFidelity = Math.round(imageFidelity * 100);
          state.subjectFidelity = Math.round(humanFidelity * 100);
        }

        // 处理清晰度和宽高设置（即梦4.5等模型支持）
        if (payload.resolution !== undefined) {
          state.resolution = payload.resolution;
        }
        if (payload.width !== undefined && payload.width > 0) {
          state.width = payload.width;
        }
        if (payload.height !== undefined && payload.height > 0) {
          state.height = payload.height;
        }
        // 处理图像质量设置（GPT Image 2 等模型支持）
        if (payload.quality !== undefined) {
          state.quality = payload.quality;
        } else {
          state.quality = undefined;
        }
      } catch (error) {
        console.error('应用图片payload时发生错误:', error);
      }
    });
  },
});

export const {
  setPrompt,
  setModelPrompt,
  setSelectedModel,
  setAspectRatio,
  setCreativeMode,
  setGenerationCount,
  setRefAsset,
  clearSavedRefAsset,
  setRefAssetArr,
  setRefAssetArrList,
  deleteAssetArr,
  setRefType,
  setFaceFidelity,
  setSubjectFidelity,
  setReferenceStrength,
  setResolution,
  setWidth,
  setHeight,
  setQuality,
  restoreDefaultModel,
  resetToInitialState
} = imageGenerationSlice.actions;

export default imageGenerationSlice.reducer;
